import { useCallback } from 'react';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';
import {
  SynthOIPCImpactChartType,
  SynthOIPCImpactLine,
  SynthOIPCImpactBarGroup,
  SynthOIPutCallImpactConfig,
  DEFAULT_SYNTH_OI_PC_IMPACT_CONFIG,
} from 'types';

const linesParser = parseAsArrayOf(
  parseAsStringEnum<SynthOIPCImpactLine>(Object.values(SynthOIPCImpactLine)),
);
const barsParser = parseAsArrayOf(
  parseAsStringEnum<SynthOIPCImpactBarGroup>(
    Object.values(SynthOIPCImpactBarGroup),
  ),
);

const SYNTH_OI_PCI_QUERY_KEYS = {
  spci_type: parseAsStringEnum<SynthOIPCImpactChartType>(
    Object.values(SynthOIPCImpactChartType),
  ).withDefault(DEFAULT_SYNTH_OI_PC_IMPACT_CONFIG.chartType),
  spci_lines_gamma: linesParser.withDefault(
    DEFAULT_SYNTH_OI_PC_IMPACT_CONFIG.linesByChartType[
      SynthOIPCImpactChartType.Gamma
    ],
  ),
  spci_lines_delta: linesParser.withDefault(
    DEFAULT_SYNTH_OI_PC_IMPACT_CONFIG.linesByChartType[
      SynthOIPCImpactChartType.Delta
    ],
  ),
  spci_bars_gamma: barsParser.withDefault(
    DEFAULT_SYNTH_OI_PC_IMPACT_CONFIG.barGroupsByChartType[
      SynthOIPCImpactChartType.Gamma
    ],
  ),
  spci_bars_delta: barsParser.withDefault(
    DEFAULT_SYNTH_OI_PC_IMPACT_CONFIG.barGroupsByChartType[
      SynthOIPCImpactChartType.Delta
    ],
  ),
  spci_bars_oi: barsParser.withDefault(
    DEFAULT_SYNTH_OI_PC_IMPACT_CONFIG.barGroupsByChartType[
      SynthOIPCImpactChartType.OpenInterest
    ],
  ),
  spci_kl: parseAsBoolean.withDefault(
    DEFAULT_SYNTH_OI_PC_IMPACT_CONFIG.showKeyLevels,
  ),
};

export function useSynthOIPutCallImpactConfig(): [
  SynthOIPutCallImpactConfig,
  (patch: Partial<SynthOIPutCallImpactConfig>) => void,
] {
  const [params, setParams] = useQueryStates(SYNTH_OI_PCI_QUERY_KEYS);

  const config: SynthOIPutCallImpactConfig = {
    chartType: params.spci_type,
    linesByChartType: {
      [SynthOIPCImpactChartType.Gamma]: params.spci_lines_gamma,
      [SynthOIPCImpactChartType.Delta]: params.spci_lines_delta,
      [SynthOIPCImpactChartType.OpenInterest]: [],
    },
    barGroupsByChartType: {
      [SynthOIPCImpactChartType.Gamma]: params.spci_bars_gamma,
      [SynthOIPCImpactChartType.Delta]: params.spci_bars_delta,
      [SynthOIPCImpactChartType.OpenInterest]: params.spci_bars_oi,
    },
    showKeyLevels: params.spci_kl,
  };

  const updateConfig = useCallback(
    (patch: Partial<SynthOIPutCallImpactConfig>) => {
      const urlPatch: Partial<typeof params> = {};
      if (patch.chartType != null) {
        urlPatch.spci_type = patch.chartType;
      }
      if (patch.linesByChartType != null) {
        const byType = patch.linesByChartType;
        if (byType[SynthOIPCImpactChartType.Gamma] != null) {
          urlPatch.spci_lines_gamma = byType[SynthOIPCImpactChartType.Gamma];
        }
        if (byType[SynthOIPCImpactChartType.Delta] != null) {
          urlPatch.spci_lines_delta = byType[SynthOIPCImpactChartType.Delta];
        }
      }
      if (patch.barGroupsByChartType != null) {
        const byType = patch.barGroupsByChartType;
        if (byType[SynthOIPCImpactChartType.Gamma] != null) {
          urlPatch.spci_bars_gamma = byType[SynthOIPCImpactChartType.Gamma];
        }
        if (byType[SynthOIPCImpactChartType.Delta] != null) {
          urlPatch.spci_bars_delta = byType[SynthOIPCImpactChartType.Delta];
        }
        if (byType[SynthOIPCImpactChartType.OpenInterest] != null) {
          urlPatch.spci_bars_oi = byType[SynthOIPCImpactChartType.OpenInterest];
        }
      }
      if (patch.showKeyLevels != null) {
        urlPatch.spci_kl = patch.showKeyLevels;
      }
      void setParams(urlPatch);
    },
    [setParams],
  );

  return [config, updateConfig];
}
